import React from 'react'
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  useTheme,
} from '@mui/material'
import { useSystemEvents } from '../hooks'
import { UptimeStatusCard } from './UptimeStatusCard'
import { VersionInfoCard } from './VersionInfoCard'
import { EventTimeline } from './EventTimeline'
import { DataAgeIndicator } from './DataAgeIndicator'
import { ServiceUptimeTimeline } from './ServiceUptimeTimeline'
import { SaveAsPdfButton } from './SaveAsPdfButton'
import { getApiUrl } from '../apollo'

export const StatusPage: React.FC = () => {
  const theme = useTheme()
  const { data, loading, error, fetchedAt, dataAgeSeconds, isStale, refetch } = useSystemEvents()

  const apiUrl = getApiUrl()
  const isNonProduction =
    !apiUrl.includes('api.allotrac.io') ||
    apiUrl.includes('develop') ||
    apiUrl.includes('beta') ||
    apiUrl.includes('test')

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={48} />
      </Box>
    )
  }

  if (error && !data) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error Loading Status</AlertTitle>
          Unable to fetch system status. Please try again later.
          <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>
            {error.message}
          </Typography>
        </Alert>
      </Container>
    )
  }

  if (!data) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          <AlertTitle>No Data Available</AlertTitle>
          System status information is currently unavailable.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Allotrac Status"
            sx={{
              height: 48,
              // In light mode, invert white logo to dark (#00263a)
              filter:
                theme.palette.mode === 'light'
                  ? 'brightness(0) saturate(100%) invert(10%) sepia(50%) saturate(1000%) hue-rotate(170deg) brightness(30%)'
                  : 'none',
            }}
          />
          {isNonProduction && (
            <Chip label={data.stage.toUpperCase()} color="warning" size="small" />
          )}
        </Box>

        <Typography variant="h5" component="h2" color="text.secondary" sx={{ mb: 2 }}>
          Real-time status of Allotrac services
        </Typography>

        <DataAgeIndicator
          fetchedAt={fetchedAt}
          dataAgeSeconds={dataAgeSeconds}
          isStale={isStale}
          loading={loading}
          onRefresh={refetch}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <SaveAsPdfButton />
        </Box>

        {isNonProduction && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Viewing status for <strong>{apiUrl}</strong> environment
          </Alert>
        )}
      </Box>

      {/* Current Status */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Current Status
        </Typography>
        <UptimeStatusCard uptimeEvent={data.uptime.current} />
      </Box>

      {/* Service Uptime Timelines */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Uptime over the past 90 days
        </Typography>
        <ServiceUptimeTimeline
          serviceName="Web Application"
          currentStatus={data.uptime.current.status}
          historicalEvents={data.uptime.historical}
          serviceKey="webapp"
        />
        <ServiceUptimeTimeline
          serviceName="API"
          currentStatus={data.uptime.current.status}
          historicalEvents={data.uptime.historical}
          serviceKey="api"
        />
        <ServiceUptimeTimeline
          serviceName="Mobile App"
          currentStatus={data.uptime.current.status}
          historicalEvents={data.uptime.historical}
          serviceKey="mobile"
        />
      </Box>

      {/* Version Info - hidden when NOT_SET */}
      {data.versioning.current.versionNumber !== 'NOT_SET' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            System Version
          </Typography>
          <VersionInfoCard versionEvent={data.versioning.current} />
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Event Timeline */}
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Event History
        </Typography>
        <EventTimeline
          uptimeHistory={data.uptime.historical.slice(1)} // Skip current (first in historical)
          uptimePlanned={data.uptime.planned}
          versioningHistory={data.versioning.historical.slice(1)} // Skip current
        />
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 6, mb: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          © {new Date().getFullYear()} Allotrac Pty Ltd. All rights reserved.
        </Typography>
      </Box>
    </Container>
  )
}
